import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '@/shared/api';
import {
  Search,
  Network,
  FolderOpen,
  Gavel,
  Loader2,
  ChevronRight,
  Building2,
  MapPin,
  Box,
  Banknote,
  CalendarClock,
  User as UserIcon,
  ExternalLink,
} from 'lucide-react';

type View = 'search' | 'entities' | 'perkara' | 'heatmap';

const SUBNAV: { key: View; label: string; roman: string; icon: any }[] = [
  { key: 'search',   label: 'Cari Lintas BAP',    roman: 'I',   icon: Search },
  { key: 'entities', label: 'Eksplorasi Entitas', roman: 'II',  icon: Network },
  { key: 'perkara',  label: 'Perkara',            roman: 'III', icon: FolderOpen },
  { key: 'heatmap',  label: 'Heatmap Pasal',      roman: 'IV',  icon: Gavel },
];

const ENTITY_TYPE_STYLE: Record<string, { label: string; color: string; border: string; Icon: any }> = {
  PERSON:       { label: 'Orang',      color: 'text-sev-high',     border: 'border-sev-high',     Icon: UserIcon },
  ORGANIZATION: { label: 'Organisasi', color: 'text-bluestamp',    border: 'border-bluestamp',    Icon: Building2 },
  LOCATION:     { label: 'Lokasi',     color: 'text-sev-medium',   border: 'border-sev-medium',   Icon: MapPin },
  OBJECT:       { label: 'Objek',      color: 'text-ink-2',        border: 'border-ink-2',        Icon: Box },
  MONEY:        { label: 'Uang',       color: 'text-olive',        border: 'border-olive',        Icon: Banknote },
  EVENT:        { label: 'Event',      color: 'text-maroon',       border: 'border-maroon',       Icon: CalendarClock },
};

export default function AnalisaPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = (searchParams.get('view') as View) || 'search';

  const setView = (v: View) => setSearchParams({ view: v }, { replace: true });

  const cur = SUBNAV.find((s) => s.key === view) || SUBNAV[0];
  const Icon = cur.icon;

  return (
    <div>
      <div className="mb-4 pb-2 border-b-2 border-ink flex items-baseline gap-3 flex-wrap">
        <Icon className="w-5 h-5 text-brass self-center" />
        <h1 className="font-display font-bold text-[22px] leading-tight tracking-tight">{cur.label}</h1>
        <span className="font-mono text-[10px] uppercase tracking-widest text-ink-3">— Analisa · Lintas Berkas</span>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-ink-3">cari · entitas · perkara · heatmap</span>
      </div>

      {view === 'search' && <SearchView />}
      {view === 'entities' && <EntitiesView />}
      {view === 'perkara' && <PerkaraView />}
      {view === 'heatmap' && <HeatmapView />}
    </div>
  );
}

// ============ B1. CROSS-DOC SEARCH ============

interface SearchHit {
  chunk_id: string;
  bap_id: string;
  bap_number: string | null;
  case_id: string | null;
  satker: string;
  classification: string;
  page_start: number;
  page_end: number;
  section: string | null;
  score: number;
  snippet: string;
}

function SearchView() {
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');

  const searchMut = useMutation({
    mutationFn: async (q: string) => {
      const resp = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/analisa/search`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Dev-User': 'admin@netra.local' },
          body: JSON.stringify({ query: q, limit: 20 }),
        },
      );
      if (!resp.ok) throw new Error(await resp.text());
      return resp.json() as Promise<{ query: string; count: number; hits: SearchHit[] }>;
    },
  });

  const doSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    setSubmitted(query.trim());
    searchMut.mutate(query.trim());
  };

  return (
    <section>
      <form onSubmit={doSearch} className="flex gap-2 mb-6">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Cari fakta, pasal, nama, tanggal di semua BAP..."
          className="flex-1 px-4 py-2.5 border border-ink-line font-mono text-[12px] bg-paper text-ink focus:outline-none focus:border-brass"
        />
        <button
          type="submit"
          disabled={!query.trim() || searchMut.isPending}
          className="px-5 py-2.5 font-mono text-[11px] uppercase tracking-wider font-semibold bg-ink text-paper hover:bg-brass transition disabled:opacity-50 flex items-center gap-2"
        >
          {searchMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          Cari
        </button>
      </form>

      {searchMut.isError && (
        <div className="p-4 border-2 border-crimson bg-crimson/5 font-mono text-xs text-crimson">
          ⚠ Gagal: {String(searchMut.error)}
        </div>
      )}

      {searchMut.data && (
        <>
          <div className="mb-4 font-mono text-[11px] uppercase tracking-wider text-ink-3">
            Query "<strong className="text-brass">{submitted}</strong>" · {searchMut.data.count} hit
          </div>
          {searchMut.data.hits.length === 0 && (
            <p className="font-mono text-xs text-ink-3 italic py-8 text-center">Tidak ada hasil.</p>
          )}
          <div className="space-y-2">
            {searchMut.data.hits.map((h) => (
              <Link
                to={`/berkas/${h.bap_id}?view=ringkasan`}
                key={h.chunk_id}
                className="block bg-paper-2/40 border border-ink-line hover:border-brass hover:bg-brass-soft/30 p-4 transition"
              >
                <div className="flex items-center gap-2 mb-2 font-mono text-[10px] uppercase tracking-wider">
                  <span className="font-bold text-ink">{h.bap_number || h.bap_id.slice(0, 8)}</span>
                  <span className="text-ink-3">·</span>
                  <span className="text-bluestamp">{h.satker}</span>
                  <span className="text-ink-3">·</span>
                  <span className="text-maroon">{h.classification.replace('_', ' ')}</span>
                  <span className="text-ink-3">· Hal. {h.page_start}{h.page_end !== h.page_start ? `-${h.page_end}` : ''}</span>
                  <span className="ml-auto text-brass">score {h.score.toFixed(3)}</span>
                  <ChevronRight className="w-3 h-3 text-ink-3" />
                </div>
                <p className="font-serif text-[13px] leading-snug text-ink-2 line-clamp-3">
                  {h.snippet}
                </p>
              </Link>
            ))}
          </div>
        </>
      )}

      {!searchMut.data && !searchMut.isPending && (
        <div className="text-center py-12 font-mono text-xs text-ink-3">
          <Search className="w-10 h-10 mx-auto mb-3 opacity-30" />
          Masukkan query untuk cari lintas semua BAP yang ter-ingest.
          <div className="mt-3 text-[10px] uppercase tracking-widest">
            Tip: coba "penggelapan", "Pasal 372", nama saksi, tanggal kejadian
          </div>
        </div>
      )}
    </section>
  );
}

// ============ B2. ENTITY EXPLORATION ============

interface EntityRow {
  entity_type: string;
  canonical_name: string;
  bap_count: number;
  total_mentions: number;
  roles: string[];
  avg_confidence: number;
}

function EntitiesView() {
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const { data: entities, isLoading } = useQuery<EntityRow[]>({
    queryKey: ['analisa-entities', typeFilter],
    queryFn: () => api(`/api/v1/analisa/entities?limit=100${typeFilter ? `&entity_type=${typeFilter}` : ''}`),
  });

  return (
    <section>
      <div className="flex gap-2 flex-wrap mb-5">
        <button
          onClick={() => setTypeFilter(null)}
          className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider font-bold border ${
            !typeFilter ? 'bg-ink text-paper border-ink' : 'border-ink-line text-ink-2 hover:border-ink'
          }`}
        >
          Semua
        </button>
        {Object.entries(ENTITY_TYPE_STYLE).map(([t, ts]) => {
          const Icon = ts.Icon;
          const active = typeFilter === t;
          return (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider font-bold border flex items-center gap-1.5 ${
                active ? 'bg-ink text-paper border-ink' : `${ts.border} ${ts.color} hover:bg-paper-2`
              }`}
            >
              <Icon className="w-3 h-3" />
              {ts.label}
            </button>
          );
        })}
      </div>

      {isLoading && <p className="font-mono text-xs text-ink-2">Memuat...</p>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          {entities && entities.length === 0 && (
            <p className="font-mono text-xs text-ink-3 italic">Belum ada entitas terdaftar.</p>
          )}
          {entities && entities.length > 0 && (
            <div className="border border-ink-line max-h-[600px] overflow-y-auto">
              {entities.map((e) => {
                const ts = ENTITY_TYPE_STYLE[e.entity_type] || ENTITY_TYPE_STYLE.PERSON;
                const Icon = ts.Icon;
                const active = selected === e.canonical_name;
                return (
                  <button
                    key={`${e.entity_type}-${e.canonical_name}`}
                    onClick={() => setSelected(e.canonical_name)}
                    className={`w-full grid grid-cols-[auto_1fr_auto] gap-2 items-center px-3 py-2 border-b border-ink-line/60 last:border-b-0 text-left transition ${
                      active ? 'bg-brass-soft/40' : 'hover:bg-brass-soft/20'
                    }`}
                  >
                    <Icon className={`w-3.5 h-3.5 ${ts.color}`} />
                    <div>
                      <div className="font-display font-bold text-[13px] text-ink">{e.canonical_name}</div>
                      <div className="font-mono text-[9px] uppercase tracking-wider text-ink-3">
                        {ts.label}
                        {e.roles.length > 0 && <> · {e.roles[0]}</>}
                      </div>
                    </div>
                    <div className="text-right font-mono text-[10px]">
                      <div className="text-ink font-bold">×{e.total_mentions}</div>
                      <div className="text-ink-3">{e.bap_count} BAP</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div>
          {selected ? (
            <EntityMentions name={selected} />
          ) : (
            <div className="text-center py-12 font-mono text-xs text-ink-3 border border-dashed border-ink-line">
              <Network className="w-8 h-8 mx-auto mb-2 opacity-40" />
              Pilih entitas di kiri untuk melihat semua BAP terkait.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

interface EntityMention {
  entity_id: string;
  entity_type: string;
  canonical_name: string;
  aliases: string[];
  role: string | null;
  description: string | null;
  mention_count: number;
  page_refs: number[];
  confidence: number;
  bap_id: string;
  bap_number: string | null;
  case_id: string | null;
  satker: string;
  classification: string;
}

function EntityMentions({ name }: { name: string }) {
  const { data, isLoading } = useQuery<EntityMention[]>({
    queryKey: ['entity-mentions', name],
    queryFn: () => api(`/api/v1/analisa/entities/${encodeURIComponent(name)}/mentions`),
  });

  if (isLoading) return <p className="font-mono text-xs text-ink-2">Memuat...</p>;
  if (!data || data.length === 0) return <p className="font-mono text-xs text-ink-3 italic">Tidak ada data.</p>;

  return (
    <div>
      <div className="mb-3 pb-3 border-b border-ink-line">
        <h3 className="font-display font-bold text-[18px] tracking-tight">{name}</h3>
        <p className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mt-1">
          {data.length} BAP terkait
        </p>
      </div>

      <div className="space-y-3 max-h-[560px] overflow-y-auto">
        {data.map((m) => (
          <Link
            to={`/berkas/${m.bap_id}?view=entitas`}
            key={m.entity_id}
            className="block bg-paper-2/40 border border-ink-line hover:border-brass hover:bg-brass-soft/30 p-3 transition"
          >
            <div className="flex items-baseline gap-2 mb-1">
              <div className="font-display font-bold text-[14px] text-ink">{m.bap_number || m.bap_id.slice(0, 8)}</div>
              <span className="font-mono text-[10px] text-bluestamp">{m.satker}</span>
              <span className="font-mono text-[10px] text-maroon ml-auto">×{m.mention_count}</span>
              <ExternalLink className="w-3 h-3 text-ink-3" />
            </div>
            {m.role && (
              <div className="font-mono text-[10px] uppercase tracking-wider text-bluestamp mb-1">
                Peran: {m.role}
              </div>
            )}
            {m.description && (
              <p className="font-serif text-[12px] text-ink-2 leading-snug mb-1">{m.description}</p>
            )}
            {m.page_refs.length > 0 && (
              <div className="font-mono text-[10px] text-ink-3">Hal. {m.page_refs.join(', ')}</div>
            )}
            {m.aliases.length > 0 && (
              <div className="font-mono text-[10px] text-ink-3 mt-1">
                alias: {m.aliases.slice(0, 3).join(' · ')}
              </div>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}

// ============ B3. PERKARA ============

interface PerkaraRow {
  case_id: string;
  bap_count: number;
  total_pages: number;
  satker: string;
  max_classification: string;
  findings_count: number;
  violations_count: number;
  inconsistencies_count: number;
  baps: Array<{ bap_id: string; bap_number: string | null; satker: string; classification: string; status: string }>;
}

function PerkaraView() {
  const { data, isLoading } = useQuery<PerkaraRow[]>({
    queryKey: ['analisa-perkara'],
    queryFn: () => api('/api/v1/analisa/perkara'),
  });

  return (
    <section>
      {isLoading && <p className="font-mono text-xs text-ink-2">Memuat...</p>}

      {data && data.length === 0 && (
        <p className="font-mono text-xs text-ink-3 italic">Belum ada perkara dengan case_id terdaftar.</p>
      )}

      {data && data.length > 0 && (
        <div className="space-y-4">
          {data.map((p) => (
            <div key={p.case_id} className="bg-paper-2/40 border border-ink-line p-5">
              <div className="grid grid-cols-[1fr_auto] gap-4 mb-4 pb-3 border-b border-ink-line">
                <div>
                  <div className="font-display font-bold text-[20px] text-ink">{p.case_id}</div>
                  <div className="font-mono text-[11px] text-ink-3 uppercase tracking-wider mt-1">
                    {p.satker} · Max klasifikasi: {p.max_classification.replace('_', ' ')}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display font-bold text-[32px] tabular-nums leading-none">{p.bap_count}</div>
                  <div className="font-mono text-[10px] text-ink-3 uppercase tracking-wider">BAP · {p.total_pages} hal</div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <Stat label="Temuan" value={p.findings_count} />
                <Stat label="Pasal Indikatif" value={p.violations_count} />
                <Stat label="Kontradiksi" value={p.inconsistencies_count} />
              </div>

              <div className="label-mono mb-2">BAP dalam Perkara Ini</div>
              <div className="grid grid-cols-2 gap-2">
                {p.baps.map((b) => (
                  <Link
                    to={`/berkas/${b.bap_id}`}
                    key={b.bap_id}
                    className="flex items-center gap-2 px-3 py-2 border border-ink-line hover:border-brass hover:bg-brass-soft/30 font-mono text-[11px] transition"
                  >
                    <FolderOpen className="w-3 h-3 text-brass" />
                    <span className="font-bold text-ink">{b.bap_number || b.bap_id.slice(0, 8)}</span>
                    <span className="text-ink-3 ml-auto">{b.status}</span>
                    <ChevronRight className="w-3 h-3 text-ink-3" />
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="border border-ink-line/60 p-3">
      <div className="label-mono mb-1">{label}</div>
      <div className="font-display font-bold text-[22px] tabular-nums leading-none">{value}</div>
    </div>
  );
}

// ============ B4. HEATMAP PASAL × SATKER ============

interface HeatmapRow {
  regulation_id: string;
  regulation_name: string;
  satker: string;
  hits: number;
  avg_confidence: number;
}

function HeatmapView() {
  const { data, isLoading } = useQuery<HeatmapRow[]>({
    queryKey: ['analisa-heatmap'],
    queryFn: () => api('/api/v1/analisa/pasal-heatmap'),
  });

  if (isLoading) return <p className="font-mono text-xs text-ink-2">Memuat...</p>;

  const pasals = Array.from(new Set((data || []).map((d) => d.regulation_name)));
  const satkers = Array.from(new Set((data || []).map((d) => d.satker)));
  const cell = new Map<string, HeatmapRow>();
  (data || []).forEach((d) => cell.set(`${d.regulation_name}|${d.satker}`, d));

  const maxHits = Math.max(1, ...(data || []).map((d) => d.hits));

  return (
    <section>
      {(!data || data.length === 0) && (
        <p className="font-mono text-xs text-ink-3 italic py-8">Belum ada data violations untuk di-aggregate.</p>
      )}

      {data && data.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border border-ink-line">
            <thead>
              <tr className="bg-paper-2/50">
                <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-3 border-r border-ink-line">
                  Pasal \ Satker
                </th>
                {satkers.map((s) => (
                  <th
                    key={s}
                    className="text-center px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-3 border-r border-ink-line/60 last:border-r-0"
                  >
                    {s}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pasals.map((p) => (
                <tr key={p} className="border-t border-ink-line/60">
                  <td className="px-3 py-2 font-display font-bold text-[13px] text-ink border-r border-ink-line">
                    {p}
                  </td>
                  {satkers.map((s) => {
                    const c = cell.get(`${p}|${s}`);
                    const intensity = c ? c.hits / maxHits : 0;
                    return (
                      <td
                        key={s}
                        className="text-center px-3 py-2 border-r border-ink-line/40 last:border-r-0 font-mono text-[11px]"
                        style={{
                          background: intensity > 0 ? `hsl(var(--maroon) / ${0.15 + intensity * 0.6})` : 'transparent',
                          color: intensity > 0.4 ? 'hsl(var(--paper))' : undefined,
                        }}
                        title={c ? `${c.hits} hits · conf ${(c.avg_confidence * 100).toFixed(0)}%` : '-'}
                      >
                        {c ? <strong>{c.hits}</strong> : <span className="text-ink-3">—</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>

          <p className="mt-3 font-mono text-[10px] uppercase tracking-wider text-ink-3">
            Intensitas warna = volume hits. Hover cell untuk confidence.
          </p>
        </div>
      )}
    </section>
  );
}
